import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight, ScrollText } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { AuditEntry } from "@/api/generated/AuditEntry"
import type { JsonValue } from "@/api/generated/serde_json/JsonValue"
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITY_TYPES,
  type AuditFilters,
  useAuditEntry,
  useAuditLog,
} from "@/api/queries/audit"
import { useSession } from "@/api/queries/session"
import { CodeBlock } from "@/components/app/code-block"
import { createColumns, DataTable } from "@/components/app/data-table"
import { KeyValueList } from "@/components/app/key-value"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState, ErrorState, LoadingState } from "@/components/app/states"
import { StatusBadge, type StatusTone } from "@/components/app/status-badge"
import { UserAvatar } from "@/components/app/user-avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Switch } from "@/components/ui/switch"
import { formatDateTime } from "@/lib/format"
import { requireCap } from "@/lib/route-guards"

interface AuditSearch {
  page?: number
  size?: number
  q?: string
  entity_type?: string
  action?: string
  from?: string
  to?: string
  all?: boolean
  entry?: number
}

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined)
const num = (v: unknown) => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined
}

export const Route = createFileRoute("/_app/settings/audit")({
  beforeLoad: requireCap("admin"),
  staticData: { titleKey: "nav.items.audit" },
  validateSearch: (s: Record<string, unknown>): AuditSearch => ({
    page: num(s.page),
    size: num(s.size),
    q: str(s.q),
    entity_type: str(s.entity_type),
    action: str(s.action),
    from: str(s.from),
    to: str(s.to),
    all: s.all === true || s.all === "true" ? true : undefined,
    entry: num(s.entry),
  }),
  component: AuditPage,
})

const ACTION_TONE: Record<string, StatusTone> = {
  create: "success",
  update: "info",
  delete: "danger",
  login: "neutral",
  logout: "neutral",
  access: "neutral",
  execute: "accent",
}

/** "YYYY-MM-DD" (local) → ISO at local start/end of that day. */
function dayBound(day: string | undefined, end: boolean): string | undefined {
  if (!day) return undefined
  const d = new Date(`${day}T00:00:00`)
  if (Number.isNaN(d.getTime())) return undefined
  if (end) d.setHours(23, 59, 59, 999)
  return d.toISOString()
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

function AuditPage() {
  const { t } = useTranslation()
  const session = useSession()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const page = search.page ?? 1
  const size = search.size ?? 50
  const [query, setQuery] = useState(search.q ?? "")
  const debounced = useDebounced(query, 300)

  const setSearch = (patch: Partial<AuditSearch>, resetPage = true) =>
    void navigate({
      search: (prev) => ({ ...prev, ...patch, ...(resetPage ? { page: undefined } : {}) }),
      replace: true,
    })

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the debounced value
  useEffect(() => {
    if ((debounced || undefined) !== search.q) setSearch({ q: debounced || undefined })
  }, [debounced])

  const filters: AuditFilters = {
    limit: size,
    offset: (page - 1) * size,
    q: search.q,
    entity_type: search.entity_type,
    action: search.action,
    from: dayBound(search.from, false),
    to: dayBound(search.to, true),
    all_tenants: search.all,
  }
  const log = useAuditLog(filters)
  const hasFilters = !!(search.q || search.entity_type || search.action || search.from || search.to)

  const entityLabel = (type: string) => t(`audit.entities.${type}` as "audit.entities.tenant", type)
  const actionLabel = (action: string) => t(`audit.actions.${action}` as "audit.actions.create", action)

  // biome-ignore lint/correctness/useExhaustiveDependencies: label helpers only depend on t
  const columns = useMemo(
    () =>
      createColumns<AuditEntry>((c) => [
        c.accessor("created_at", {
          header: t("audit.columns.time"),
          enableSorting: false,
          cell: ({ getValue }) => (
            <RelativeTime value={getValue()} className="text-muted-foreground tabular-nums" />
          ),
        }),
        c.accessor("actor_label", {
          header: t("audit.columns.actor"),
          enableSorting: false,
          cell: ({ row }) => (
            <div className="flex items-center gap-2">
              <UserAvatar name={row.original.actor_label} size="sm" className="size-5" />
              <span className="truncate">{row.original.actor_label}</span>
            </div>
          ),
        }),
        c.accessor("action", {
          header: t("audit.columns.action"),
          enableSorting: false,
          cell: ({ getValue }) => (
            <StatusBadge tone={ACTION_TONE[getValue()] ?? "neutral"}>{actionLabel(getValue())}</StatusBadge>
          ),
        }),
        c.accessor("entity_type", {
          header: t("audit.columns.entity"),
          enableSorting: false,
          cell: ({ row }) => (
            <div className="min-w-0">
              <span className="text-muted-foreground">{entityLabel(row.original.entity_type)}</span>{" "}
              <span className="font-medium">{row.original.entity_repr || `#${row.original.entity_id}`}</span>
            </div>
          ),
        }),
        c.accessor((e) => (e.changes && typeof e.changes === "object" ? Object.keys(e.changes).length : 0), {
          id: "changes",
          header: t("audit.columns.changes"),
          meta: { label: t("audit.columns.changes"), align: "end" },
          enableSorting: false,
          cell: ({ getValue }) =>
            getValue() > 0 ? (
              <span className="text-muted-foreground text-xs tabular-nums">
                {t("audit.fieldsChanged", { count: getValue() })}
              </span>
            ) : null,
        }),
      ]),
    [t],
  )

  return (
    <PageContainer width="wide">
      <PageHeader title={t("audit.title")} description={t("audit.description")} icon={ScrollText} />
      <DataTable
        label={t("audit.title")}
        data={log.data?.items}
        columns={columns}
        getRowId={(e) => String(e.id)}
        loading={log.isPending}
        error={log.error}
        onRetry={() => void log.refetch()}
        searchable
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder={t("audit.search")}
        serverPagination={{
          pageIndex: page - 1,
          pageSize: size,
          rowCount: log.data?.total ?? 0,
          onChange: (p) =>
            setSearch(
              {
                page: p.pageIndex + 1 > 1 ? p.pageIndex + 1 : undefined,
                size: p.pageSize === 50 ? undefined : p.pageSize,
              },
              false,
            ),
        }}
        onRowClick={(e) => setSearch({ entry: e.id }, false)}
        isRowActive={(e) => e.id === search.entry}
        toolbar={
          <>
            <Select
              value={search.entity_type ?? "all"}
              onValueChange={(v) => setSearch({ entity_type: v === "all" ? undefined : (v as string) })}
            >
              <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("audit.columns.entity")}>
                <SelectValue>
                  {(v: string) => (v === "all" ? t("audit.allEntities") : entityLabel(v))}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("audit.allEntities")}</SelectItem>
                {AUDIT_ENTITY_TYPES.map((e) => (
                  <SelectItem key={e} value={e}>
                    {entityLabel(e)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={search.action ?? "all"}
              onValueChange={(v) => setSearch({ action: v === "all" ? undefined : (v as string) })}
            >
              <SelectTrigger size="sm" className="h-8 w-36" aria-label={t("audit.columns.action")}>
                <SelectValue>
                  {(v: string) => (v === "all" ? t("audit.allActions") : actionLabel(v))}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("audit.allActions")}</SelectItem>
                {AUDIT_ACTIONS.map((a) => (
                  <SelectItem key={a} value={a}>
                    {actionLabel(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                aria-label={t("audit.from")}
                value={search.from ?? ""}
                max={search.to}
                onChange={(e) => setSearch({ from: e.target.value || undefined })}
                className="h-8 w-36 text-xs"
              />
              <ArrowRight className="text-muted-foreground size-3.5" aria-hidden />
              <Input
                type="date"
                aria-label={t("audit.to")}
                value={search.to ?? ""}
                min={search.from}
                onChange={(e) => setSearch({ to: e.target.value || undefined })}
                className="h-8 w-36 text-xs"
              />
            </div>
            {session.user?.is_platform_admin && (
              <Label className="text-muted-foreground gap-2 text-xs font-normal">
                <Switch
                  size="sm"
                  checked={!!search.all}
                  onCheckedChange={(v) => setSearch({ all: v || undefined })}
                />
                {t("audit.allOrganizations")}
              </Label>
            )}
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQuery("")
                  setSearch({
                    q: undefined,
                    entity_type: undefined,
                    action: undefined,
                    from: undefined,
                    to: undefined,
                  })
                }}
              >
                {t("audit.clearFilters")}
              </Button>
            )}
          </>
        }
        emptyState={
          <EmptyState
            variant="plain"
            icon={ScrollText}
            title={hasFilters ? t("audit.emptyFiltered.title") : t("audit.empty.title")}
            description={hasFilters ? t("audit.emptyFiltered.description") : t("audit.empty.description")}
          />
        }
      />
      <AuditDrawer
        id={search.entry}
        preview={log.data?.items.find((e) => e.id === search.entry)}
        onClose={() => setSearch({ entry: undefined }, false)}
        entityLabel={entityLabel}
        actionLabel={actionLabel}
      />
    </PageContainer>
  )
}

function renderValue(v: JsonValue | undefined) {
  if (v === null || v === undefined || v === "")
    return <span className="text-muted-foreground italic">∅</span>
  if (typeof v === "object") return <code className="font-mono text-xs break-all">{JSON.stringify(v)}</code>
  if (typeof v === "boolean") return <code className="font-mono text-xs">{String(v)}</code>
  return <span className="break-words">{String(v)}</span>
}

/** Renders `{field: [old, new]}` as a before/after table; anything else as JSON. */
function ChangesDiff({ changes }: { changes: JsonValue | null }) {
  const { t } = useTranslation()
  if (changes === null || changes === undefined)
    return <p className="text-muted-foreground text-sm">{t("audit.noChanges")}</p>
  const isDiff =
    typeof changes === "object" &&
    !Array.isArray(changes) &&
    Object.values(changes).every((v) => Array.isArray(v) && v.length === 2)
  if (!isDiff) return <CodeBlock code={JSON.stringify(changes, null, 2)} language="json" maxHeight={420} />
  const entries = Object.entries(changes as Record<string, [JsonValue, JsonValue]>)
  if (entries.length === 0) return <p className="text-muted-foreground text-sm">{t("audit.noChanges")}</p>
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-muted/40 text-muted-foreground text-xs">
          <tr>
            <th className="w-1/3 px-3 py-2 text-left font-medium">{t("audit.diff.field")}</th>
            <th className="px-3 py-2 text-left font-medium">{t("audit.diff.before")}</th>
            <th className="px-3 py-2 text-left font-medium">{t("audit.diff.after")}</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {entries.map(([field, [before, after]]) => (
            <tr key={field} className="align-top">
              <td className="px-3 py-2 font-mono text-xs break-all">{field}</td>
              <td className="bg-destructive/[0.04] px-3 py-2 text-[0.8125rem] line-through decoration-destructive/40">
                {renderValue(before)}
              </td>
              <td className="bg-success/[0.05] px-3 py-2 text-[0.8125rem]">{renderValue(after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AuditDrawer({
  id,
  preview,
  onClose,
  entityLabel,
  actionLabel,
}: {
  id: number | undefined
  preview: AuditEntry | undefined
  onClose: () => void
  entityLabel: (t: string) => string
  actionLabel: (a: string) => string
}) {
  const { t } = useTranslation()
  const detail = useAuditEntry(preview ? undefined : id)
  const entry = preview ?? detail.data
  return (
    <Sheet open={id != null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full gap-0 sm:max-w-xl">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="flex items-center gap-2">
            {entry && (
              <StatusBadge tone={ACTION_TONE[entry.action] ?? "neutral"}>
                {actionLabel(entry.action)}
              </StatusBadge>
            )}
            <span className="truncate">
              {entry
                ? `${entityLabel(entry.entity_type)} ${entry.entity_repr || `#${entry.entity_id}`}`
                : t("audit.entry")}
            </span>
          </SheetTitle>
          <SheetDescription>
            {entry ? formatDateTime(entry.created_at, "PPpp") : t("common.loading")}
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {!entry && detail.isPending ? (
            <LoadingState />
          ) : !entry ? (
            <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
          ) : (
            <div className="space-y-6">
              <KeyValueList
                items={[
                  { label: t("audit.columns.actor"), value: entry.actor_label },
                  { label: t("audit.columns.action"), value: actionLabel(entry.action) },
                  { label: t("audit.columns.entity"), value: entityLabel(entry.entity_type) },
                  { label: t("audit.entityId"), value: entry.entity_id, mono: true, copy: entry.entity_id },
                  { label: t("audit.ip"), value: entry.ip ?? "—", mono: true },
                  { label: t("audit.entryId"), value: `#${entry.id}`, mono: true },
                ]}
              />
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">{t("audit.changes")}</h3>
                <ChangesDiff changes={entry.changes} />
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
